import oneSpaceApi from '../lib/onespace-api';

export interface SpotlightData {
  id: string;
  type: string;
  content: {
    title: string;
    subtitle?: string | null;
    description?: string | null;
    leftContent?: {
      type: string;
      content: string;
      alt?: string;
    };
    survey?: {
      question?: string;
      options?: string[];
      questions?: Array<{
        id: string;
        question: string;
        type: string;
        options?: string[];
      }>;
    } | null;
    form?: {
      fields: Array<{
        id: string;
        label: string;
        type: string;
        required?: boolean;
        options?: Array<{ value: string; label: string }>;
        placeholder?: string;
        helpText?: string;
      }>;
    } | null;
    carousel?: {
      steps?: Array<{
        title: string;
        subtitle?: string;
        content: string;
      }>;
      slides?: any[];
    } | null;
    externalLink?: {
      text?: string | null;
      url?: string | null;
    } | null;
    agreement?: {
      text?: string | null;
      required?: boolean;
      requireSignature?: boolean;
    } | null;
    visibility_settings?: {
      schedule_type: string;
      days: number[];
      time_ranges: Array<{ start: string; end: string }>;
    };
  };
  sender?: {
    name: string;
    avatar?: string;
    role?: string;
  } | null;
  social?: {
    likes: number;
    comments: Array<{
      id: string;
      text: string;
      author?: {
        name: string;
        avatar?: string;
      };
      timestamp: string;
      visibility: 'everyone' | 'sender';
    }>;
    allowComments: boolean;
    allowQuestions: boolean;
  };
  deadline?: {
    date: string;
    text?: string;
  } | null;
  required: boolean;
  visibility: {
    start_date: string;
    end_date?: string | null;
    settings: any;
  };
  is_published: boolean;
  published_at?: string | null;
  is_readonly: boolean;
  show_once: boolean;
  priority: number;
  sequence_info?: {
    sequence_id: string;
    sequence_order: number;
    waiting_days: number;
  };
  created_at: string;
  updated_at: string;
}

/**
 * Fetches the available Spotlights from the API
 * @param preferredUuid Optional preferred spotlight UUID to prioritize
 * @returns Promise with the Spotlights data
 */
export const fetchSpotlights = async (preferredUuid?: string): Promise<SpotlightData | null> => {
  try {
    // console.log('Fetching spotlights using OneSpace API client', preferredUuid ? `with preferred UUID: ${preferredUuid}` : '');
    
    // Build URL with optional preferred_uuid parameter
    let url = '/api/spotlights/next';
    if (preferredUuid) {
      url += `?preferred_uuid=${encodeURIComponent(preferredUuid)}`;
    }
    
    const response = await oneSpaceApi.get(url);
    
    // console.log('Raw API response:', response);
    // console.log('API response data structure:', JSON.stringify(response.data, null, 2));
    
    // Handle different response formats
    if (response && response.data) {
      // The API returns {data: SpotlightData} - extract the inner data property
      if (response.data.data && typeof response.data.data === 'object') {
        // console.log('API returned a wrapped spotlight object in data.data');
        return response.data.data;
      }
      
      // If API returns the spotlight directly
      if (response.data.id && response.data.content) {
        // console.log('API returned a spotlight object directly');
        return response.data;
      }
    }
    
    // No valid data found
    console.log('No valid spotlight data found in response');
    return null;
  } catch (error) {
    console.error('Error fetching spotlights:', error);
    // Error handling is now centralized in the API client
    // The offline state will be updated via the event listener
    throw error;
  }
};

/**
 * Marks a Spotlight as agreed to
 * @param spotlightId The ID of the Spotlight to mark as agreed
 * @param data Additional data like survey responses or form data
 */
export const agreeToSpotlight = async (spotlightId: string, data?: any): Promise<void> => {
  try {
    console.log(`Agreeing to spotlight ${spotlightId} using OneSpace API client`);
    await oneSpaceApi.post(`/api/spotlights/${spotlightId}/agree`, data || {});
    console.log(`Spotlight ${spotlightId} agreed to successfully`);
  } catch (error) {
    console.error('Error agreeing to spotlight:', error);
    // Error handling is now centralized in the API client
    throw error;
  }
};

/**
 * Fetches comments for a specific Spotlight
 * @param spotlightId The ID of the Spotlight to get comments for
 * @returns Promise with the comments data
 */
export const fetchSpotlightComments = async (spotlightId: string) => {
  try {
    console.log(`Fetching comments for spotlight ${spotlightId}`);
    const response = await oneSpaceApi.get(`/api/spotlights/${spotlightId}/comments`);
    
    console.log('Comments API response:', response);
    
    if (response && response.data) {
      return response.data;
    }
    
    return [];
  } catch (error) {
    console.error(`Error fetching comments for spotlight ${spotlightId}:`, error);
    // Error handling is now centralized in the API client
    return [];
  }
};

/**
 * Adds a comment to a specific Spotlight
 * @param spotlightId The ID of the Spotlight to add a comment to
 * @param comment The comment text
 * @param visibility The visibility of the comment ('everyone' or 'sender')
 * @returns Promise with the created comment data
 */
export const addSpotlightComment = async (
  spotlightId: string, 
  comment: string, 
  visibility: 'everyone' | 'sender' = 'everyone'
) => {
  try {
    console.log(`Adding comment to spotlight ${spotlightId}`);
    const response = await oneSpaceApi.post(`/api/spotlights/${spotlightId}/comments`, {
      text: comment,
      visibility
    });
    
    console.log('Add comment API response:', response);
    
    if (response && response.data) {
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error(`Error adding comment to spotlight ${spotlightId}:`, error);
    // Error handling is now centralized in the API client
    throw error;
  }
};

/**
 * Like a spotlight
 * @param spotlightId The ID of the Spotlight to like
 * @returns Promise with the like response data
 */
export const likeSpotlight = async (spotlightId: string) => {
  try {
    console.log(`Liking spotlight ${spotlightId}`);
    const response = await oneSpaceApi.post(`/api/spotlights/${spotlightId}/like`, {
      liked: true
    });
    
    console.log('Like API response:', response);
    
    if (response && response.data) {
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error(`Error liking spotlight ${spotlightId}:`, error);
    // Error handling is now centralized in the API client
    throw error;
  }
};

/**
 * Dislike a spotlight
 * @param spotlightId The ID of the Spotlight to dislike
 * @returns Promise with the dislike response data
 */
export const dislikeSpotlight = async (spotlightId: string) => {
  try {
    console.log(`Disliking spotlight ${spotlightId}`);
    const response = await oneSpaceApi.post(`/api/spotlights/${spotlightId}/dislike`);
    
    console.log('Dislike API response:', response);
    
    if (response && response.data) {
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error(`Error disliking spotlight ${spotlightId}:`, error);
    // Error handling is now centralized in the API client
    throw error;
  }
};

/**
 * Get the current like status of a spotlight for the current user
 * @param spotlightId The ID of the Spotlight to check like status
 * @returns Promise with the like status data
 */
export const getSpotlightLikeStatus = async (spotlightId: string) => {
  try {
    console.log(`Fetching like status for spotlight ${spotlightId}`);
    const response = await oneSpaceApi.get(`/api/spotlights/${spotlightId}/likes`);
    
    console.log('Like status API response:', response);
    
    if (response && response.data) {
      return {
        hasLiked: response.data.hasLiked || false,
        likesCount: response.data.likesCount || response.data.count || 0
      };
    }
    
    return {
      hasLiked: false,
      likesCount: 0
    };
  } catch (error) {
    console.error(`Error fetching like status for spotlight ${spotlightId}:`, error);
    // Error handling is now centralized in the API client
    return {
      hasLiked: false,
      likesCount: 0
    };
  }
}; 